import { useCallback, useEffect, useState } from 'react';
import { Modal as ModalBootstrap } from 'react-bootstrap';
import classNames from 'classnames';

import { ChevronLeftIcon, ChevronRightIcon, CloseLargeIcon } from '../../icons';
import styles from './_index.module.scss';

export interface LightboxImage {
  alt: string;
  caption: string;
  src: string;
}

export interface LightboxProps {
  currentImageIndex?: number;
  images?: LightboxImage[];
  isActive: boolean;
  onClose?: () => void;
}

const Lightbox = ({
  currentImageIndex = 0,
  images,
  isActive,
  onClose,
  ...props
}: LightboxProps) => {
  const [index, setIndex] = useState(currentImageIndex);

  const selectedImage = images ? images[index] : null;
  const title = selectedImage ? selectedImage.caption : null;

  const prevImage = useCallback(() => {
    if (images?.length) {
      setIndex((currIndex) => (currIndex - 1 + images.length) % images.length);
    }
  }, [images]);

  const nextImage = useCallback(() => {
    if (images?.length) {
      setIndex((currIndex) => (currIndex + 1) % images.length);
    }
  }, [images]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isActive) return;

      if (event.key === 'ArrowLeft') {
        prevImage();
      } else if (event.key === 'ArrowRight') {
        nextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, onClose, prevImage, nextImage]);

  useEffect(() => {
    setIndex(currentImageIndex);
  }, [currentImageIndex]);

  return (
    <ModalBootstrap
      animation={false}
      backdropClassName={styles['lightbox-backdrop']}
      centered
      className={classNames(styles['lightbox'])}
      fullscreen
      onHide={onClose}
      show={isActive}
      {...props}
    >
      <div className={styles['lightbox-content-wrapper']}>
        {(title || onClose) && (
          <div className={styles['lightbox-header']}>
            {title && <h1>{title}</h1>}
            {onClose && (
              <button
                aria-label="Close"
                className={classNames(styles['lightbox-close'])}
                data-testid="lightbox-close"
                onClick={onClose}
              >
                <CloseLargeIcon className={styles['lightbox-close-icon']} />
              </button>
            )}
          </div>
        )}
        <ModalBootstrap.Body className={styles['lightbox-body']}>
          {images && images.length > 1 && (
            <button
              aria-label="Previous Image"
              className={classNames(
                styles['lightbox-navigation'],
                styles['lightbox-navigation-left']
              )}
              data-testid="lightbox-previous"
              onClick={prevImage}
            >
              <ChevronLeftIcon className={styles['lightbox-rotate']} />
            </button>
          )}

          <div>
            {selectedImage && (
              <img
                alt={selectedImage?.alt}
                className={classNames(styles['lightbox-image'])}
                src={selectedImage?.src}
              />
            )}

            {!selectedImage && (
              <div className={styles['lightbox-image-placeholder']}>
                <p>No image available</p>
              </div>
            )}
          </div>

          {images && images.length > 1 && (
            <button
              aria-label="Next Image"
              className={classNames(
                styles['lightbox-navigation'],
                styles['lightbox-navigation-right']
              )}
              data-testid="lightbox-next"
              onClick={nextImage}
            >
              <ChevronRightIcon className={styles['lightbox-rotate']} />
            </button>
          )}
        </ModalBootstrap.Body>
      </div>
    </ModalBootstrap>
  );
};

export default Lightbox;
